// Save a Magic Studio generation into the Asset Lab as a real asset.
//
// Reuses the same file-creation primitive as the Upload module
// (api.fs.uploadFile, which creates the file node in a single request), but
// targets an arbitrary category instead
// of the currently-selected one, so it deliberately bypasses the Redux uploader
// (which is wired to the selected category). AI images are small, so the
// single-part upload endpoint is always sufficient — no multipart needed.

import { api } from '../../../../general/apiScenarios';
import { appendTag, bindTags, loadTags, Tag } from '../../../api';
import { blobImageMime, withImageExtension } from '../imageFormat';
import { dataUrlToBlob } from '../localOps/download';
import { AiImageMeta, embedAiMetadata } from '../metadata/aiMetadata';
import { Destination } from './types';

const AI_TAG_NAME = 'AI Generated';

const matchesAiTag = (tags: Tag[]): number | undefined =>
  tags.find((t) => t.name.trim().toLowerCase() === AI_TAG_NAME.toLowerCase())?.id;

// Find the "AI Generated" tag id — project-scoped first, then unscoped (the tag
// may be global / not yet associated with the project).
async function resolveAiTagId(projectId: number): Promise<number | undefined> {
  const scoped = await loadTags({ projectIds: [projectId] });
  if (scoped.success) {
    const id = matchesAiTag(scoped.data.response);
    if (id !== undefined) return id;
  }
  const all = await loadTags({});
  return all.success ? matchesAiTag(all.data.response) : undefined;
}

// Resolve (creating if needed) the "AI Generated" tag and bind it to the asset.
// Best-effort: the asset is already saved, so a tag failure must never surface
// as a save failure — the caller swallows it.
async function ensureAiTagAndBind(assetId: number, projectId: number): Promise<void> {
  let tagId = await resolveAiTagId(projectId);
  if (tagId === undefined) {
    // appendTag's response is untyped: some backends return the created tag,
    // some don't — try its id, otherwise re-resolve from a fresh list.
    const created = await appendTag(AI_TAG_NAME);
    const maybe = created.success ? (created.data.response as Partial<Tag> | undefined) : undefined;
    tagId = typeof maybe?.id === 'number' ? maybe.id : await resolveAiTagId(projectId);
  }
  if (tagId === undefined) return; // give up quietly — the asset is saved regardless
  await bindTags({ tag_ids: [tagId], asset_ids: [assetId] });
}

// Upload a generated image (data: / blob: URL) into `dest`, returning the new
// asset's node id. Then attach the "AI Generated" tag (best-effort). Throws if
// the upload itself fails. When `meta` is given, the AI-provenance XMP is
// embedded into the file bytes so the saved asset carries it (in addition to
// the Asset Lab tag) — see metadata/aiMetadata.ts.
//
// `name` is what the user typed / saw in the save popover; its extension is
// re-pointed at the format the bytes really are before the file is created, so
// a JPEG generation can never land in the catalog named *.png (the Asset Lab
// derives the asset's type from the uploaded filename).
export async function uploadGenerationToAssets({
  imageUrl,
  name,
  dest,
  meta,
}: {
  imageUrl: string;
  name: string;
  dest: Destination;
  meta?: AiImageMeta;
}): Promise<number> {
  let blob = await dataUrlToBlob(imageUrl);
  if (meta) {
    try {
      blob = await embedAiMetadata(blob, meta);
    } catch {
      // Embedding must never fail a save — fall back to the raw bytes.
    }
  }
  const mime = await blobImageMime(blob);
  const file = new File([blob], withImageExtension(name, mime), {
    type: mime || blob.type || 'image/png',
  });
  const parentNodeId = Number.parseInt(dest.categoryId, 10);

  const result = await api.fs.uploadFile(file, parentNodeId, undefined, undefined, true);
  if (!result.success) {
    const reason = Array.isArray(result.response?.data)
      ? result.response.data.join(' ')
      : 'Upload failed';
    throw new Error(reason);
  }
  const assetId = result.response.node.id as number;

  try {
    await ensureAiTagAndBind(assetId, dest.projectId);
  } catch {
    // Tagging is best-effort — never fail a successful save because of it.
  }

  return assetId;
}
